import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { AssignDialog } from "@/components/AssignDialog";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Truck, Loader2 } from "lucide-react";

const EMPTY = {
  asset_tag: "", serial_number: "", device_type: "laptop", brand: "", model_name: "",
  cpu: "", ram: "", storage: "", condition: "good", status: "available",
  purchase_date: "", cost: "", vendor: "", notes: "",
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function Inventory() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteId, setDeleteId] = useState(null);
  const [assignItem, setAssignItem] = useState(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["inventory"],
    queryFn: async () => (await api.get("/inventory")).data,
  });
  const { data: clients = [] } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => (await api.get("/clients")).data,
  });

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const body = { ...payload, cost: parseFloat(payload.cost) || 0, purchase_date: payload.purchase_date || null };
      if (editing) return (await api.put(`/inventory/${editing.id}`, body)).data;
      return (await api.post("/inventory", body)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success(editing ? "Item updated" : "Item added");
      setFormOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => (await api.delete(`/inventory/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success("Item deleted");
      setDeleteId(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const openAdd = () => { setEditing(null); setForm(EMPTY); setFormOpen(true); };
  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...EMPTY, ...item, cost: item.cost ?? "", purchase_date: item.purchase_date ? item.purchase_date.slice(0, 10) : "" });
    setFormOpen(true);
  };

  const filtered = useMemo(() => {
    return items.filter((i) => {
      if (tab !== "all" && i.status !== tab) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return [i.asset_tag, i.serial_number, i.brand, i.model_name, i.cpu, i.vendor]
        .filter(Boolean).some((v) => v.toLowerCase().includes(q));
    });
  }, [items, search, tab]);

  const availableItems = items.filter((i) => i.status === "available");

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  return (
    <div data-testid="inventory-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight text-[#0F172A]">Master Inventory</h1>
          <p className="text-slate-500 mt-1">{items.length} units in your rental fleet.</p>
        </div>
        <Button onClick={openAdd} className="bg-[#0A2540] hover:bg-[#1E3A5F]" data-testid="add-inventory-button">
          <Plus className="h-4 w-4 mr-1.5" /> Add Item
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search asset tag, brand, specs…" className="pl-9" data-testid="inventory-search" />
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="inventory-filter-tabs">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="available">Available</TabsTrigger>
            <TabsTrigger value="rented">Rented</TabsTrigger>
            <TabsTrigger value="under_repair">Repair</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <Card className="border-slate-200 shadow-none rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead>Asset Tag</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Device</TableHead>
                <TableHead>Specs</TableHead>
                <TableHead>Condition</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Purchased</TableHead>
                <TableHead className="text-right">Cost (₹)</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={10} className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#0A2540]" /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-12 text-slate-500">No items found.</TableCell></TableRow>
              ) : (
                filtered.map((i) => (
                  <TableRow key={i.id} data-testid={`inventory-row-${i.asset_tag}`} className="hover:bg-slate-50">
                    <TableCell className="font-mono font-medium text-[#0F172A]">{i.asset_tag}</TableCell>
                    <TableCell className="capitalize">{i.device_type}</TableCell>
                    <TableCell>{[i.brand, i.model_name].filter(Boolean).join(" ") || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{[i.cpu, i.ram, i.storage].filter(Boolean).join(" · ") || "—"}</TableCell>
                    <TableCell className="capitalize">{i.condition}</TableCell>
                    <TableCell><StatusBadge status={i.status} testid={`status-${i.asset_tag}`} /></TableCell>
                    <TableCell className="text-sm text-slate-600">{fmtDate(i.purchase_date)}</TableCell>
                    <TableCell className="text-right font-mono">{i.cost ? Number(i.cost).toLocaleString("en-IN") : "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{i.vendor || "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {i.status === "available" && (
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-[#0A2540]" title="Assign to client" onClick={() => setAssignItem(i)} data-testid={`assign-${i.asset_tag}`}>
                            <Truck className="h-4 w-4" />
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(i)} data-testid={`edit-${i.asset_tag}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => setDeleteId(i.id)} data-testid={`delete-${i.asset_tag}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="inventory-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Item" : "Add Inventory Item"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5"><Label>Asset Tag *</Label><Input value={form.asset_tag} onChange={set("asset_tag")} placeholder="PCP-0001" data-testid="form-asset-tag" /></div>
            <div className="space-y-1.5"><Label>Serial Number</Label><Input value={form.serial_number} onChange={set("serial_number")} data-testid="form-serial" /></div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.device_type} onValueChange={set("device_type")}>
                <SelectTrigger data-testid="form-device-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="laptop">Laptop</SelectItem><SelectItem value="desktop">Desktop</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="rented">Rented</SelectItem>
                  <SelectItem value="under_repair">Under Repair</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Brand</Label><Input value={form.brand} onChange={set("brand")} placeholder="Dell" data-testid="form-brand" /></div>
            <div className="space-y-1.5"><Label>Model</Label><Input value={form.model_name} onChange={set("model_name")} placeholder="Latitude 5420" data-testid="form-model" /></div>
            <div className="space-y-1.5"><Label>CPU</Label><Input value={form.cpu} onChange={set("cpu")} placeholder="i5-1135G7" data-testid="form-cpu" /></div>
            <div className="space-y-1.5"><Label>RAM</Label><Input value={form.ram} onChange={set("ram")} placeholder="16GB" data-testid="form-ram" /></div>
            <div className="space-y-1.5"><Label>Storage</Label><Input value={form.storage} onChange={set("storage")} placeholder="512GB SSD" data-testid="form-storage" /></div>
            <div className="space-y-1.5">
              <Label>Condition</Label>
              <Select value={form.condition} onValueChange={set("condition")}>
                <SelectTrigger data-testid="form-condition"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem><SelectItem value="good">Good</SelectItem>
                  <SelectItem value="fair">Fair</SelectItem><SelectItem value="poor">Poor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Purchase Date</Label><Input type="date" value={form.purchase_date} onChange={set("purchase_date")} data-testid="form-purchase-date" /></div>
            <div className="space-y-1.5"><Label>Cost (₹)</Label><Input type="number" value={form.cost} onChange={set("cost")} placeholder="45000" data-testid="form-cost" /></div>
            <div className="space-y-1.5"><Label>Vendor</Label><Input value={form.vendor} onChange={set("vendor")} placeholder="Supplier name" data-testid="form-vendor" /></div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline" data-testid="form-cancel">Cancel</Button></DialogClose>
            <Button onClick={() => { if (!form.asset_tag) { toast.error("Asset tag is required"); return; } saveMutation.mutate(form); }} disabled={saveMutation.isPending} className="bg-[#0A2540] hover:bg-[#1E3A5F]" data-testid="form-save">
              {saveMutation.isPending ? "Saving…" : editing ? "Update" : "Add Item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this item?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the unit from your inventory.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteMutation.mutate(deleteId)} data-testid="delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AssignDialog open={!!assignItem} onOpenChange={(o) => !o && setAssignItem(null)} clients={clients} availableItems={availableItems} presetItemId={assignItem?.id} />
    </div>
  );
}
